import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { PublicProfile, ProfileStats, PaginatedUsers, PaginatedCourses } from '../types/profile';
import { apiClient } from '../services/apiClient';

interface ProfileState {
    profile: PublicProfile | null;
    stats: ProfileStats | null;
    courses: PaginatedCourses | null;
    followers: PaginatedUsers | null;
    following: PaginatedUsers | null;
    loading: boolean;
    error: string | null;
}

interface ProfileActions {
    fetchProfile: (userId: string) => Promise<void>;
    fetchStats: (userId: string) => Promise<void>;
    fetchCourses: (userId: string, page?: number) => Promise<void>;
    fetchFollowers: (userId: string, page?: number) => Promise<void>;
    fetchFollowing: (userId: string, page?: number) => Promise<void>;
    followUser: (userId: string) => Promise<boolean>;
    unfollowUser: (userId: string) => Promise<boolean>;
    checkFollowStatus: (userId: string) => Promise<boolean>;
    clearProfile: () => void;
}

type ProfileStore = ProfileState & ProfileActions;

// apiClient already has base URL configured

export const useProfileStore = create<ProfileStore>()(
    devtools(
        (set, _get) => ({
            // State
            profile: null,
            stats: null,
            courses: null,
            followers: null,
            following: null,
            loading: false,
            error: null,

            // Actions
            fetchProfile: async (userId: string) => {
                set({ loading: true, error: null });
                try {
                    const response = await apiClient.get<PublicProfile>(
                        `/profiles/${userId}`
                    );
                    set({ profile: response.data, loading: false });
                } catch (error: any) {
                    set({
                        loading: false,
                        error: error.response?.data?.message || 'Failed to fetch profile',
                    });
                }
            },

            fetchStats: async (userId: string) => {
                try {
                    const response = await apiClient.get<ProfileStats>(
                        `/profiles/${userId}/stats`
                    );
                    set({ stats: response.data });
                } catch (error: any) {
                    console.error('Failed to fetch stats:', error);
                }
            },

            fetchCourses: async (userId: string, page = 1) => {
                try {
                    const response = await apiClient.get<PaginatedCourses>(
                        `/profiles/${userId}/courses`,
                        { params: { page, limit: 12 } }
                    );

                    if (page === 1) {
                        set({ courses: response.data });
                    } else {
                        set((state) => ({
                            courses: state.courses
                                ? {
                                      ...response.data,
                                      items: [...state.courses.items, ...response.data.items],
                                  }
                                : response.data,
                        }));
                    }
                } catch (error: any) {
                    console.error('Failed to fetch courses:', error);
                }
            },

            fetchFollowers: async (userId: string, page = 1) => {
                try {
                    const response = await apiClient.get<PaginatedUsers>(
                        `/profiles/${userId}/followers`,
                        { params: { page, limit: 20 } }
                    );

                    if (page === 1) {
                        set({ followers: response.data });
                    } else {
                        set((state) => ({
                            followers: state.followers
                                ? {
                                      ...response.data,
                                      items: [...state.followers.items, ...response.data.items],
                                  }
                                : response.data,
                        }));
                    }
                } catch (error: any) {
                    console.error('Failed to fetch followers:', error);
                }
            },

            fetchFollowing: async (userId: string, page = 1) => {
                try {
                    const response = await apiClient.get<PaginatedUsers>(
                        `/profiles/${userId}/following`,
                        { params: { page, limit: 20 } }
                    );

                    if (page === 1) {
                        set({ following: response.data });
                    } else {
                        set((state) => ({
                            following: state.following
                                ? {
                                      ...response.data,
                                      items: [...state.following.items, ...response.data.items],
                                  }
                                : response.data,
                        }));
                    }
                } catch (error: any) {
                    console.error('Failed to fetch following:', error);
                }
            },

            followUser: async (userId: string) => {
                try {
                    await apiClient.post(`/profiles/${userId}/follow`);

                    // Update local state
                    set((state) => ({
                        profile: state.profile
                            ? {
                                  ...state.profile,
                                  isFollowing: true,
                                  followerCount: state.profile.followerCount + 1,
                              }
                            : null,
                    }));

                    return true;
                } catch (error: any) {
                    set({ error: error.response?.data?.message || 'Failed to follow user' });
                    return false;
                }
            },

            unfollowUser: async (userId: string) => {
                try {
                    await apiClient.delete(`/profiles/${userId}/follow`);

                    // Update local state
                    set((state) => ({
                        profile: state.profile
                            ? {
                                  ...state.profile,
                                  isFollowing: false,
                                  followerCount: Math.max(0, state.profile.followerCount - 1),
                              }
                            : null,
                    }));

                    return true;
                } catch (error: any) {
                    set({ error: error.response?.data?.message || 'Failed to unfollow user' });
                    return false;
                }
            },

            checkFollowStatus: async (userId: string) => {
                try {
                    const response = await apiClient.get<{ isFollowing: boolean }>(
                        `/profiles/${userId}/follow/status`
                    );
                    return response.data.isFollowing;
                } catch (error) {
                    return false;
                }
            },

            clearProfile: () => {
                set({
                    profile: null,
                    stats: null,
                    courses: null,
                    followers: null,
                    following: null,
                    error: null,
                });
            },
        }),
        { name: 'ProfileStore' }
    )
);

// Utility hooks
export const useProfile = () => useProfileStore((state) => state.profile);
export const useProfileStats = () => useProfileStore((state) => state.stats);
export const useProfileCourses = () => useProfileStore((state) => state.courses);
export const useProfileActions = () => {
    const {
        fetchProfile,
        fetchStats,
        fetchCourses,
        fetchFollowers,
        fetchFollowing,
        followUser,
        unfollowUser,
        clearProfile,
    } = useProfileStore();
    return {
        fetchProfile,
        fetchStats,
        fetchCourses,
        fetchFollowers,
        fetchFollowing,
        followUser,
        unfollowUser,
        clearProfile,
    };
};
